import {
  resolveLocalizedText,
  type TLocalizedText,
} from '../../helpers/localized-text.helpers';
import {
  DEFAULT_VOICE_UI_LANGUAGE,
  getVoiceMessages,
  VOICE_NEW_BADGE_DEFAULT_COLORS,
  VOICE_TEXT_FIELDS,
  VOICE_UI_LANGUAGES,
  type VoiceMessages,
  type VoiceTextField,
  type VoiceUILanguage,
} from '../constants/voice-messages';
import type {
  VoiceConfig,
  VoiceTextsConfig,
} from '../types/webclient-config.type';

type VoiceTextGroupValues<Value> = Record<string, Value | undefined>;

const isVoiceUILanguage = (language: string): language is VoiceUILanguage =>
  (VOICE_UI_LANGUAGES as readonly string[]).includes(language);

// A bot or visitor language with its own built-in set uses it; any other
// language uses the fallback picked in the voice settings.
export const getBuiltInVoiceLanguage = (
  language: string,
  fallbackLanguage?: VoiceUILanguage,
): VoiceUILanguage => {
  if (isVoiceUILanguage(language)) {
    return language;
  }

  return fallbackLanguage || DEFAULT_VOICE_UI_LANGUAGE;
};

const getBuiltInText = (
  messages: VoiceMessages,
  field: VoiceTextField,
): string => {
  const group = messages[field.group] as VoiceTextGroupValues<string>;
  return group[field.key] ?? '';
};

const getStoredText = (
  texts: VoiceTextsConfig | undefined,
  field: VoiceTextField,
): TLocalizedText | undefined => {
  const group = texts?.[field.group] as
    | VoiceTextGroupValues<TLocalizedText>
    | undefined;
  return group?.[field.key];
};

// True when at least one translation holds more than whitespace.
export const hasFilledVoiceText = (text: TLocalizedText): boolean => {
  if (typeof text === 'string') {
    return text.trim() !== '';
  }

  return Object.values(text).some((translation) => translation.trim() !== '');
};

// An optional text is hidden per language, so an empty translation must not
// fall through to another language the way other localized texts do. Returns
// undefined when no translation applies, which shows the built-in text.
const resolveOptionalVoiceText = (
  storedText: TLocalizedText,
  activeLanguage: string,
  defaultLanguage?: string,
): string | undefined => {
  if (typeof storedText === 'string') {
    return storedText.trim();
  }

  const translationForLanguage = storedText[activeLanguage];
  if (translationForLanguage !== undefined) {
    return translationForLanguage.trim();
  }

  const translationForDefaultLanguage = defaultLanguage
    ? storedText[defaultLanguage]
    : undefined;
  if (translationForDefaultLanguage !== undefined) {
    return translationForDefaultLanguage.trim();
  }

  return undefined;
};

// A whitespace-only translation counts as empty, so it falls back the same way
// an empty one does instead of resolving to blank.
const trimTranslations = (storedText: TLocalizedText): TLocalizedText => {
  if (typeof storedText === 'string') {
    return storedText.trim();
  }

  const trimmedTranslations: Record<string, string> = {};

  for (const [language, translation] of Object.entries(storedText)) {
    trimmedTranslations[language] = translation.trim();
  }

  return trimmedTranslations;
};

const resolveStoredVoiceText = (
  storedText: TLocalizedText,
  field: VoiceTextField,
  activeLanguage: string,
  defaultLanguage?: string,
): string | undefined => {
  if (!field.isRequired) {
    return resolveOptionalVoiceText(
      storedText,
      activeLanguage,
      defaultLanguage,
    );
  }

  const resolvedText = resolveLocalizedText(
    trimTranslations(storedText),
    activeLanguage,
    defaultLanguage,
  );

  // The form refills a blank required text before saving, so this only guards
  // stored data written some other way.
  if (!resolvedText) {
    return undefined;
  }

  return resolvedText;
};

type ResolveVoiceMessagesInput = {
  voice?: VoiceConfig;
  // The visitor's language from the flow.
  activeLanguage: string;
  // The bot's default language, used when a text has no translation for the
  // visitor's language.
  defaultLanguage?: string;
};

// The texts the widget shows: the built-in set in the visitor's language, with
// the admin's overrides from `voice.texts` on top.
export const resolveVoiceMessages = ({
  voice,
  activeLanguage,
  defaultLanguage,
}: ResolveVoiceMessagesInput): VoiceMessages => {
  const builtInMessages = getVoiceMessages(
    getBuiltInVoiceLanguage(activeLanguage, voice?.language),
  );

  const resolvedMessages: VoiceMessages = {
    ...builtInMessages,
    status: { ...builtInMessages.status },
    buttons: { ...builtInMessages.buttons },
    tabs: { ...builtInMessages.tabs },
    // The agent speaks the language set on it in ElevenLabs, not the visitor's
    // flow language, so its instruction keeps following the voice settings.
    wrapUpNotice: getVoiceMessages(voice?.language).wrapUpNotice,
  };

  for (const field of VOICE_TEXT_FIELDS) {
    const storedText = getStoredText(voice?.texts, field);

    if (storedText === undefined) {
      continue;
    }

    const resolvedText = resolveStoredVoiceText(
      storedText,
      field,
      activeLanguage,
      defaultLanguage,
    );

    if (resolvedText === undefined) {
      continue;
    }

    const group = resolvedMessages[field.group] as VoiceTextGroupValues<string>;
    group[field.key] = resolvedText;
  }

  return resolvedMessages;
};

// `undefined` means never saved, so it gets the built-in text. A stored empty
// string is how an admin hides an optional text, so it is kept.
const localizeVoiceText = (
  storedText: TLocalizedText | undefined,
  field: VoiceTextField,
  languages: string[],
  defaultLanguage: string,
  voiceLanguage?: VoiceUILanguage,
): TLocalizedText | undefined => {
  // Without bot languages the form shows no text inputs, and a prefilled plain
  // string would pin one language for every visitor once languages are added.
  if (!languages.length) {
    return storedText;
  }

  const legacyTextLanguage = defaultLanguage || languages[0];
  const translations: Record<string, string> = {};

  if (typeof storedText === 'object') {
    Object.assign(translations, storedText);
  }

  if (typeof storedText === 'string') {
    translations[legacyTextLanguage] = storedText;
  }

  for (const language of languages) {
    if (translations[language] !== undefined) {
      continue;
    }

    translations[language] = getBuiltInText(
      getVoiceMessages(getBuiltInVoiceLanguage(language, voiceLanguage)),
      field,
    );
  }

  return translations;
};

// Form default values: every editable text gets a value for every bot
// language, prefilled with the built-in text so the required ones start valid.
// A company without bot languages keeps whatever is stored.
export const localizeVoiceTexts = (
  texts: VoiceTextsConfig | undefined,
  languages: string[],
  defaultLanguage: string,
  voiceLanguage?: VoiceUILanguage,
): VoiceTextsConfig => {
  const localizedTexts: Record<string, Record<string, TLocalizedText>> = {
    status: {},
    buttons: {},
    tabs: {},
  };

  for (const field of VOICE_TEXT_FIELDS) {
    const localizedText = localizeVoiceText(
      getStoredText(texts, field),
      field,
      languages,
      defaultLanguage,
      voiceLanguage,
    );

    if (localizedText !== undefined) {
      localizedTexts[field.group][field.key] = localizedText;
    }
  }

  return localizedTexts as VoiceTextsConfig;
};

// Run before saving the form. A required text whose input was not on screen -
// a collapsed section, or the badge text after the badge was turned off - is
// not validated, so an emptied one would otherwise fail the whole save. It gets
// the built-in text back instead.
export const fillBlankRequiredVoiceTexts = <Voice extends VoiceConfig>(
  voice: Voice | undefined,
  // The bot default language. Other languages fall back to it, so it must
  // stay filled even when another translation is.
  defaultLanguage?: string,
): Voice | undefined => {
  if (!voice?.texts) {
    return voice;
  }

  const filledTexts: Record<string, Record<string, TLocalizedText>> = {};

  for (const [group, groupTexts] of Object.entries(voice.texts)) {
    filledTexts[group] = { ...groupTexts };
  }

  for (const field of VOICE_TEXT_FIELDS) {
    const storedText = getStoredText(voice.texts, field);

    if (!field.isRequired || storedText === undefined) {
      continue;
    }

    const getBuiltInTranslation = (language: string) =>
      getBuiltInText(
        getVoiceMessages(getBuiltInVoiceLanguage(language, voice.language)),
        field,
      );

    if (hasFilledVoiceText(storedText)) {
      if (typeof storedText === 'object' && defaultLanguage) {
        const defaultTranslation = storedText[defaultLanguage];

        if (defaultTranslation !== undefined && !defaultTranslation.trim()) {
          filledTexts[field.group][field.key] = {
            ...storedText,
            [defaultLanguage]: getBuiltInTranslation(defaultLanguage),
          };
        }
      }
      continue;
    }

    if (typeof storedText === 'string') {
      filledTexts[field.group][field.key] = getBuiltInText(
        getVoiceMessages(voice.language),
        field,
      );
      continue;
    }

    const builtInTranslations: Record<string, string> = {};

    for (const language of Object.keys(storedText)) {
      builtInTranslations[language] = getBuiltInTranslation(language);
    }

    filledTexts[field.group][field.key] = builtInTranslations;
  }

  return { ...voice, texts: filledTexts as VoiceTextsConfig };
};

export type VoiceNewBadge = {
  text: string;
  backgroundColor: string;
  textColor: string;
};

// The badge next to the voice tab label, or undefined when it is turned off.
export const resolveVoiceNewBadge = (
  voice: VoiceConfig | undefined,
  voiceMessages: VoiceMessages,
): VoiceNewBadge | undefined => {
  if (!voice?.newBadge?.enabled) {
    return undefined;
  }

  return {
    text: voiceMessages.tabs.newBadge,
    backgroundColor:
      voice.newBadge.backgroundColor ||
      VOICE_NEW_BADGE_DEFAULT_COLORS.backgroundColor,
    textColor:
      voice.newBadge.textColor || VOICE_NEW_BADGE_DEFAULT_COLORS.textColor,
  };
};

const isUnchangedFromBuiltIn = (
  storedText: TLocalizedText,
  field: VoiceTextField,
  voiceLanguage?: VoiceUILanguage,
): boolean => {
  if (typeof storedText === 'string') {
    return (
      storedText === getBuiltInText(getVoiceMessages(voiceLanguage), field)
    );
  }

  return Object.entries(storedText).every(
    ([language, translation]) =>
      translation ===
      getBuiltInText(
        getVoiceMessages(getBuiltInVoiceLanguage(language, voiceLanguage)),
        field,
      ),
  );
};

// The form starts prefilled with the built-in texts, so saving it as is would
// store copies of them and a later change to a default would never reach this
// config. A text still equal to its built-in in every language is left out, so
// only the admin's real changes are stored.
export const dropUnchangedVoiceTexts = <Voice extends VoiceConfig>(
  voice: Voice | undefined,
): Voice | undefined => {
  if (!voice?.texts) {
    return voice;
  }

  const changedTexts: Record<string, Record<string, TLocalizedText>> = {};

  for (const field of VOICE_TEXT_FIELDS) {
    const storedText = getStoredText(voice.texts, field);

    if (
      storedText === undefined ||
      isUnchangedFromBuiltIn(storedText, field, voice.language)
    ) {
      continue;
    }

    changedTexts[field.group] = changedTexts[field.group] ?? {};
    changedTexts[field.group][field.key] = storedText;
  }

  return { ...voice, texts: changedTexts as VoiceTextsConfig };
};

// Everything the voice texts need before a save, in order: blank required texts
// get their built-in back, then texts equal to their built-in are left out.
export const prepareVoiceTextsForSave = <Voice extends VoiceConfig>(
  voice: Voice | undefined,
  defaultLanguage?: string,
): Voice | undefined =>
  dropUnchangedVoiceTexts(fillBlankRequiredVoiceTexts(voice, defaultLanguage));
